import React, { useState, useEffect, useMemo } from "react";
import Helmet from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import FadeLoader from "react-spinners/FadeLoader";
import AddRoundMultiplierModal from "components/Modals/AddRoundMultiplier";
import Table from "components/Table";
import Columns from "./Columns";
import {
  deleteRoundMultiplier,
  getRoundMultiplierList,
  roundMultiplierStatusChange,
} from "store/roundMultiplier/actions";
import PermissionGate from "components/PermissionGate";
import usePermission from "hooks/usePermission";
import RoundMultiplierModal from "components/Modals/roundMultiplier/roundMultiplierModal";

const List = () => {
  const dispatch = useDispatch();

  const { loading, roundMultiplierList, totalCount } = useSelector(
    ({ RoundMultiplier }) => ({
      loading: RoundMultiplier?.loading,
      roundMultiplierList: RoundMultiplier?.roundMultiplierList,
      totalCount: RoundMultiplier?.totalCount,
    }),
  );

  const { user } = useSelector((state) => state.Auth);

  const [columns, setColumns] = useState([]);
  const [roundMultiplierModalShow, setRoundMultiplierModalShow] =
    useState(false);
  const [addRoundMultiplierPop, setAddRoundMultiplierPop] = useState(false);
  const [rowData, setRowData] = useState(null);
  const [id, setId] = useState(null);

  const [filter, setFilter] = useState({
    page: 1,
    limit: 20,
    order: -1,
    orderBy: "date_created_utc",
    search: "",
  });

  const { hasPermission } = usePermission();
  const permissions = useMemo(() => ({
    add: hasPermission("add_round_multiplier"),
    edit: hasPermission("edit_round_multiplier"),
    delete: hasPermission("delete_round_multiplier"),
  }), [hasPermission]);

  // ✅ API call
  useEffect(() => {
    getApiData();
  }, [filter]);

  // ✅ Columns setup
  useEffect(() => {
    setColumns(
      Columns({
        deleteHandler,
        handleEdit,
        handleStatusToggle,
        handleViewHandler,
        permissions,
      })?.filter((item) => {
        if (
          item?.text === "Action" &&
          !permissions.edit &&
          !permissions.delete
        ) {
          return false;
        }
        return true;
      }),
    );
  }, [permissions.edit, permissions.delete]);

  const getApiData = () => {
    dispatch(getRoundMultiplierList({ data: filter }));
  };

  const deleteHandler = (id) => {
    const userConfirmed = window.confirm(
      "Are you sure you want to delete this Round Multiplier?",
    );

    if (!userConfirmed) return;

    dispatch(
      deleteRoundMultiplier({
        data: { _id: id },
        callback: (err) => {
          if (!err) getApiData();
        },
      }),
    );
  };

  const handleEdit = (info) => {
    setRowData(info);
    setAddRoundMultiplierPop(true);
  };

  const handleStatusToggle = (row) => {
    const updatedStatus = row.status === "active" ? "inactive" : "active";

    const userConfirmed = window.confirm(
      `Are you sure you want to ${updatedStatus} this Round Multiplier?`,
    );

    if (!userConfirmed) return;

    dispatch(
      roundMultiplierStatusChange({
        data: {
          _id: row._id,
          status: updatedStatus,
        },
        callback: (err) => {
          if (!err) getApiData();
        },
      }),
    );
  };

  // ✅ FIXED View Handler
  const handleViewHandler = (info) => {
    let id = info?._id;

    // Handle ObjectId edge case
    if (typeof id === "object" && id !== null) {
      id = id?.$oid || id?.toString();
    }

    if (!id || typeof id !== "string") {
      console.error("Invalid ID:", id);
      return;
    }

    setId(id);
    setRoundMultiplierModalShow(true);
  };

  return (
    <>
      {/* Add / Edit Modal */}
      {addRoundMultiplierPop && (
        <AddRoundMultiplierModal
          rowData={rowData}
          show={addRoundMultiplierPop}
          loading={loading}
          handleClose={() => {
            setRowData(null);
            setAddRoundMultiplierPop(false);
            getApiData();
          }}
        />
      )}

      {/* Loader */}
      {loading && (
        <div className="main-loader">
          <FadeLoader size={1000} />
        </div>
      )}

      {/* View Modal */}
      {roundMultiplierModalShow && (
        <RoundMultiplierModal
          show={roundMultiplierModalShow}
          handleClose={() => {
            setRoundMultiplierModalShow(false);
            setId(null);
          }}
          title={"Round Multiplier Details"}
          id={id}
        />
      )}

      <Helmet title={"Round Multiplier"} />

      <section className="section">
        <div className="section-header">
          <h1>Round Multiplier</h1>
        </div>

        <div className="section-body">
          <div className="card">
            <div className="row justify-content-between">
              <div className="col-6">
                <div className="card-header">
                  <h2 className="section-title">Round Multiplier List</h2>
                </div>
              </div>

              <PermissionGate permission="add_round_multiplier">
                <div className="col-6">
                  <div className="card-header d-flex justify-content-end align-items-center">
                    <button
                      className="btn btn-primary"
                      onClick={() => setAddRoundMultiplierPop(true)}
                    >
                      Add Round Multiplier
                    </button>
                  </div>
                </div>
              </PermissionGate>
            </div>
          </div>

          {!loading && roundMultiplierList?.length > 0 && (
            <Table
              data={roundMultiplierList}
              columns={columns}
              total={totalCount}
              toggleConfirmModal={deleteHandler}
            />
          )}

          {!loading && roundMultiplierList?.length === 0 && (
            <p style={{ textAlign: "center" }}>No Round Multiplier Found</p>
          )}
        </div>
      </section>
    </>
  );
};

export default List;
